/**
 * Promo / discount banner config (Firestore-backed, admin-editable).
 *
 * Lives on the same `publicSiteContent` collection as the login footer
 * content so it inherits the existing security rule (public read,
 * admin/superuser write — see firestore.rules §publicSiteContent).
 */

export const PUBLIC_SITE_CONTENT_COLLECTION = 'publicSiteContent';
export const PROMO_BANNER_DOC_ID = 'promoBanner';

/** localStorage key used to remember which version the user dismissed. */
export const PROMO_BANNER_DISMISS_KEY = 'ecointel.promoBanner.dismissedVersion';

export const PROMO_BANNER_VARIANTS = ['teal', 'amber', 'emerald', 'slate'];

/**
 * Defaults shipped with the build. The banner is OFF until an admin enables
 * it from the Admin Console → Promo banner tab. The default copy below is
 * just a placeholder so the editor preview shows something useful.
 */
export const DEFAULT_PROMO_BANNER = Object.freeze({
  enabled: false,
  message: 'Founding member offer, save on your subscription with code below.',
  couponCodes: [],
  ctaLabel: 'Subscribe & save',
  ctaUrl: '/checkout',
  variant: 'teal',
  // Bumped automatically on save in the editor; used as the dismissal
  // cache-buster so users who closed an old banner see the new one.
  version: 1,
});

/** Max promotion codes shown on the banner (each is individually copyable). */
export const PROMO_BANNER_MAX_COUPON_CODES = 6;

function isNonEmptyString(v) {
  return typeof v === 'string' && v.trim().length > 0;
}

/**
 * Split a legacy single coupon field that may contain multiple codes separated
 * by commas, slashes, or "or" / "OR" (common admin typo from BugHerd #28).
 */
function splitLegacyCouponString(raw) {
  return String(raw)
    .split(/\s*(?:,|\/|;|\bor\b|\s+OR\s+)\s*/i)
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean);
}

/**
 * Normalise coupon codes from Firestore. Prefers `couponCodes` array; falls
 * back to legacy `couponCode` string (including multi-code strings).
 */
export function normalizeCouponCodes(data) {
  if (data != null && Array.isArray(data.couponCodes)) {
    return data.couponCodes
      .map((c) => (typeof c === 'string' ? c.trim().toUpperCase() : ''))
      .filter(Boolean)
      .slice(0, PROMO_BANNER_MAX_COUPON_CODES);
  }
  if (data != null && isNonEmptyString(data.couponCode)) {
    const split = splitLegacyCouponString(data.couponCode);
    return split.slice(0, PROMO_BANNER_MAX_COUPON_CODES);
  }
  return [];
}

/**
 * Coerce a Firestore document into a safe shape the UI can render. Always
 * returns an object — even if the doc is missing or malformed — so the
 * banner component can stay dumb and just check `enabled`.
 */
export function resolvePromoBanner(data) {
  if (data == null || typeof data !== 'object') {
    return { ...DEFAULT_PROMO_BANNER };
  }
  const variant = PROMO_BANNER_VARIANTS.includes(data.variant)
    ? data.variant
    : DEFAULT_PROMO_BANNER.variant;
  const versionNum = Number(data.version);
  const version = Number.isFinite(versionNum) && versionNum > 0
    ? Math.floor(versionNum)
    : DEFAULT_PROMO_BANNER.version;
  return {
    enabled: data.enabled === true,
    message: isNonEmptyString(data.message) ? data.message : DEFAULT_PROMO_BANNER.message,
    couponCodes: normalizeCouponCodes(data),
    ctaLabel: isNonEmptyString(data.ctaLabel) ? data.ctaLabel : DEFAULT_PROMO_BANNER.ctaLabel,
    ctaUrl: isNonEmptyString(data.ctaUrl) ? data.ctaUrl : DEFAULT_PROMO_BANNER.ctaUrl,
    variant,
    version,
  };
}

/** Per-variant Tailwind classes. Kept as plain strings so the JIT keeps them. */
export const VARIANT_STYLES = Object.freeze({
  teal: {
    bar: 'bg-[#043132] text-white',
    chip: 'bg-white/15 text-white border border-white/25 hover:bg-white/25',
    cta: 'bg-white text-[#043132] hover:bg-slate-100',
    dismiss: 'text-white/70 hover:text-white',
  },
  amber: {
    bar: 'bg-amber-500 text-amber-950',
    chip: 'bg-amber-900/15 text-amber-950 border border-amber-900/25 hover:bg-amber-900/25',
    cta: 'bg-amber-950 text-amber-50 hover:bg-amber-900',
    dismiss: 'text-amber-950/70 hover:text-amber-950',
  },
  emerald: {
    bar: 'bg-emerald-700 text-white',
    chip: 'bg-white/15 text-white border border-white/25 hover:bg-white/25',
    cta: 'bg-white text-emerald-800 hover:bg-emerald-50',
    dismiss: 'text-white/70 hover:text-white',
  },
  slate: {
    bar: 'bg-slate-900 text-white',
    chip: 'bg-white/10 text-white border border-white/20 hover:bg-white/20',
    cta: 'bg-white text-slate-900 hover:bg-slate-100',
    dismiss: 'text-white/70 hover:text-white',
  },
});
